import { z } from 'zod';

import { RECORD_SORTS, listQuerySchema, sortParam } from './pagination';

/*
 * The leads inbox — ROADMAP.md §9.8, data-model.md §7.
 *
 * `./intake.ts` is the customer's half: the quote form and the contact form.
 * This is the office's half — what happens to a submission after it lands.
 *
 * **There is no message model here, and that is deliberate.** `lead_message` is
 * struck in data-model.md §7: a lead-only thread cannot survive the lead
 * becoming a client, and a customer's history has to. Correspondence is a
 * `conversation` (§8a), which serves leads, clients and strangers from one
 * model and arrives with 9A. The temptation while that is pending is a quick
 * notes thread bolted on here — that is the exact mistake the table was struck
 * for, and shipping one "temporarily" is how it comes back.
 *
 * `lead.notes` is a field on the record, not a thread. The contact form's
 * message goes in it because a one-off message from a stranger is a fact about
 * the enquiry, not a conversation with two sides.
 */

// ---------------------------------------------------------------------------
// Status and source
// ---------------------------------------------------------------------------

export const LEAD_STATUS_VALUES = ['new', 'contacted', 'qualified', 'converted', 'lost'] as const;
export type LeadStatusValue = (typeof LEAD_STATUS_VALUES)[number];

/** What a person reads. Never the stored key — CLAUDE.md §4, defect P5. */
export const LEAD_STATUS_LABELS: Record<LeadStatusValue, string> = {
  new: 'New',
  contacted: 'Contacted',
  qualified: 'Qualified',
  converted: 'Converted',
  lost: 'Lost',
};

export const LEAD_SOURCE_VALUES = [
  'contact_form',
  'quote_request',
  'referral',
  'phone',
  'walk_in',
  'manual',
  'website_import',
] as const;
export type LeadSourceValue = (typeof LEAD_SOURCE_VALUES)[number];

/**
 * Where the enquiry came from, in the office's words.
 *
 * The two website ones are named apart on purpose: a quote request carries the
 * property block that is the entire basis of a price, and a contact-form
 * message does not. Telling them apart at a glance is what `source` is for.
 */
export const LEAD_SOURCE_LABELS: Record<LeadSourceValue, string> = {
  contact_form: 'Website — message',
  quote_request: 'Website — quote request',
  referral: 'Referral',
  phone: 'Phone',
  walk_in: 'Walk-in',
  manual: 'Added by hand',
  /*
   * **Named for where the row came from, not for what the person did**, because
   * for most of them the answer is "nothing". The import creates leads out of an
   * export whose rows include newsletter signups and login profiles as well as
   * enquiries, and calling those `contact_form` would put words in five hundred
   * people's mouths. An office that cannot tell an imported row from a real
   * enquiry stops trusting both.
   */
  website_import: 'Imported from the old website',
};

/**
 * Which statuses can follow which.
 *
 * **`converted` is not in any list**, for the same reason `hired` is absent from
 * the applicant transitions: converting creates a client, and a lead marked
 * converted with no client behind it is a lie the office finds out about later.
 * `POST /:id/convert` is the only path to it.
 *
 * Everything else is reversible, because working a lead is not a ratchet — a
 * "lost" enquiry that rings back on Monday goes straight back into the list.
 */
export const LEAD_STATUS_TRANSITIONS: Record<LeadStatusValue, readonly LeadStatusValue[]> = {
  new: ['contacted', 'qualified', 'lost'],
  contacted: ['qualified', 'lost', 'new'],
  qualified: ['contacted', 'lost'],
  // Terminal. A client exists; undoing that is a decision about a customer
  // record, not a status change.
  converted: [],
  lost: ['new', 'contacted', 'qualified'],
};

export const LEAD_STATUS_FILTERS = [
  { value: 'new', label: LEAD_STATUS_LABELS.new },
  { value: 'contacted', label: LEAD_STATUS_LABELS.contacted },
  { value: 'qualified', label: LEAD_STATUS_LABELS.qualified },
  { value: 'converted', label: LEAD_STATUS_LABELS.converted },
  { value: 'lost', label: LEAD_STATUS_LABELS.lost },
  { value: 'all', label: 'All' },
] as const;

// ---------------------------------------------------------------------------
// Bathrooms — the half-step that must not become a float
// ---------------------------------------------------------------------------

/**
 * `'2.5'` reads as "2.5"; `'2.0'` reads as "2".
 *
 * **A string operation, never `Number()`.** `lead_property.bathrooms` is
 * `decimal(3,1)` and mysql2 hands it back as a string, which is correct for a
 * decimal — the value travels from the form to the database and back without a
 * float ever existing. Formatting it by parsing would put one in the middle of
 * that journey for no reason, and the habit of turning a decimal into a float is
 * the one CLAUDE.md warns about for money.
 *
 * Half bathrooms are real. A rounded one is a wrong quote.
 */
export function bathroomsLabel(value: string): string {
  return value.endsWith('.0') ? value.slice(0, -2) : value;
}

// ---------------------------------------------------------------------------
// What the office reads
// ---------------------------------------------------------------------------

/**
 * One property from a quote request.
 *
 * **This block is the entire basis of the quote.** Without it the quote form is
 * a contact form wearing a quote form's title, and a twelve-property enquiry
 * from a property manager looks like a one-bedroom flat.
 */
export interface LeadPropertyView {
  id: string;
  /** "Property 1" is 0 — the form's own numbering. */
  sortOrder: number;
  bedrooms: number;
  /** A string, all the way. See `bathroomsLabel`. */
  bathrooms: string;
  bathroomsLabel: string;
  /** The stored band key. */
  squareFeetBand: string;
  /** "2,000–2,500" — the band the customer chose, never a midpoint. */
  squareFeetBandLabel: string;
}

export interface LeadListItem {
  id: string;
  /** The whole name as they typed it — see `LeadDetail`. */
  displayName: string;
  businessName: string | null;
  /**
   * **Null for a lead somebody rang in** — migration 030. Both website forms
   * require an address; a phone enquiry has none, and inventing one is P9.
   * `phone` is the alternative and the database requires one of the two.
   */
  email: string | null;
  phone: string | null;

  city: string | null;
  state: string | null;
  postalCode: string | null;

  source: LeadSourceValue;
  sourceLabel: string;
  status: LeadStatusValue;
  statusLabel: string;

  assignedToUserId: string | null;
  assignedToName: string | null;

  /** How many properties they described. Zero for a plain message. */
  propertyCount: number;

  createdAt: string;
  convertedClientId: string | null;
  convertedAt: string | null;
}

export interface LeadDetail extends LeadListItem {
  /**
   * The name exactly as it was typed.
   *
   * **A web lead puts the whole string in `first_name`** and `last_name` is
   * null — both live forms have one Name field, and splitting a single string
   * is guesswork on any name that is not exactly two words, and not undoable
   * once stored (data-model.md §7). Both parts are carried so the screen can
   * show what the person actually wrote.
   */
  firstName: string;
  lastName: string | null;

  notes: string | null;
  properties: LeadPropertyView[];
  convertedClientName: string | null;

  /**
   * Who this enquiry might already be — owner instruction, 2026-08-07.
   *
   * Empty for almost every lead. **Never a merge**, in either direction: an
   * exact email match already put the message on that person's thread, and a
   * near match did nothing at all.
   */
  duplicates: LeadDuplicateView[];

  /**
   * The thread this enquiry's message went onto, when it is not one of this
   * lead's own.
   *
   * **Null is the ordinary case** — the message opened a thread on this lead
   * and the correspondence panel finds it by `leadId`. It is set only when an
   * exact email match sent the message to an existing customer's conversation,
   * where the panel would otherwise show "No messages yet" for an enquiry that
   * plainly had one.
   */
  correspondenceConversationId: string | null;
}

// ---------------------------------------------------------------------------
// Possible duplicates — owner instruction, 2026-08-07
// ---------------------------------------------------------------------------

export const DUPLICATE_MATCH_TYPE_VALUES = ['exact_email', 'name_and_phone'] as const;
export type DuplicateMatchTypeValue = (typeof DUPLICATE_MATCH_TYPE_VALUES)[number];

export const DUPLICATE_STATUS_VALUES = ['open', 'resolved', 'dismissed'] as const;
export type DuplicateStatusValue = (typeof DUPLICATE_STATUS_VALUES)[number];

/**
 * Somebody this enquiry might already be.
 *
 * **The owner's rule is that this is never acted on automatically past the
 * exact-email case**: *"A near-match on name and phone gets flagged for me —
 * never auto-merged."* Merging two customers who turn out to be different
 * people is very hard to undo and fails silently.
 */
export interface LeadDuplicateView {
  id: string;
  candidateType: 'client' | 'lead';
  candidateId: string;
  /** Their name, resolved server-side. A candidate that no longer exists is dropped. */
  candidateName: string;
  /** Where to go to look at them. An enquiry that converted links to the customer. */
  candidateHref: string;
  candidateIsArchived: boolean;

  matchType: DuplicateMatchTypeValue;
  /** The sentence, written when the match was made. Never a raw enum — CLAUDE.md §4. */
  reason: string;

  status: DuplicateStatusValue;
  reviewedByName: string | null;
  createdAt: string;
}

export interface LeadListResponse {
  data: LeadListItem[];
  page: number;
  pageSize: number;
  total: number;
  counts: Record<LeadStatusValue | 'all', number>;
}

// ---------------------------------------------------------------------------
// Inputs
// ---------------------------------------------------------------------------

export const leadListQuerySchema = listQuerySchema.extend({
  sort: sortParam(RECORD_SORTS, 'newest'),

  status: z.enum([...LEAD_STATUS_VALUES, 'all']).default('new'),
  source: z.enum(LEAD_SOURCE_VALUES).optional(),
  /** `"me"` is resolved server-side against the signed-in admin. */
  assignedTo: z.union([z.literal('me'), z.literal('unassigned'), z.uuid()]).optional(),
});
export type LeadListQuery = z.infer<typeof leadListQuerySchema>;

/**
 * An enquiry the office takes down itself — a phone call, a referral, somebody
 * at the door.
 *
 * **This is the whole reason `phone`, `walk_in`, `referral` and `manual` are on
 * `LEAD_SOURCE_VALUES`.** They have been there since 9.8 and nothing could ever
 * write one: every lead came from the two website forms. Somebody ringing up
 * had nowhere to be recorded at all, so they were kept on paper or not at all.
 *
 * **The name is one field, matching the two web forms and `lead.first_name`'s
 * own note.** Splitting a name is guesswork that cannot be undone, and it is
 * done once, by a human, at the moment of conversion (`convertLeadSchema`).
 *
 * **An email is optional and a phone number is the alternative.** `refine`
 * below requires one of the two, mirroring `lead_contactable_chk` in migration
 * 030 — the rule is in the database so it holds for every write path, and here
 * so the person filling the form gets a sentence instead of a constraint
 * violation. Requiring an email would produce fake ones, which is P9.
 */
export const createLeadSchema = z
  .object({
    name: z.string().trim().min(1, 'Who got in touch?').max(128),
    email: z
      .union([z.email('That does not look like an email address'), z.literal('')])
      .transform((value) => (value === '' ? null : value.toLowerCase()))
      .nullable()
      .optional(),
    phone: z
      .string()
      .trim()
      .max(32)
      .transform((value) => (value.length === 0 ? null : value))
      .nullable()
      .optional(),
    businessName: z
      .string()
      .trim()
      .max(255)
      .transform((value) => (value.length === 0 ? null : value))
      .nullable()
      .optional(),
    city: z
      .string()
      .trim()
      .max(128)
      .transform((value) => (value.length === 0 ? null : value))
      .nullable()
      .optional(),
    /**
     * The office's own four. The two website sources are absent on purpose —
     * a lead typed in by an admin did not come from the website, and letting
     * this claim it did would corrupt the one field that says where enquiries
     * actually come from.
     */
    source: z.enum(['phone', 'walk_in', 'referral', 'manual']).default('phone'),
    notes: z
      .string()
      .trim()
      .max(5000)
      .transform((value) => (value.length === 0 ? null : value))
      .nullable()
      .optional(),
  })
  .refine((value) => Boolean(value.email) || Boolean(value.phone), {
    message: 'Give an email address or a phone number — we need one way to reach them.',
    path: ['phone'],
  });

export type CreateLeadInput = z.infer<typeof createLeadSchema>;

/** `converted` is absent from the enum, not merely refused. See the transitions. */
export const leadStatusSchema = z.object({
  status: z.enum(['new', 'contacted', 'qualified', 'lost']),
});
export type LeadStatusInput = z.infer<typeof leadStatusSchema>;

/** Null unassigns. Assignment says whose job it is; it is not access control. */
export const leadAssignmentSchema = z.object({
  assignedToUserId: z.uuid().nullable(),
});
export type LeadAssignmentInput = z.infer<typeof leadAssignmentSchema>;

export const leadNotesSchema = z.object({
  notes: z
    .string()
    .trim()
    .max(5000, 'That note is longer than our system can take — please shorten it.')
    .transform((value) => (value.length === 0 ? null : value))
    .nullable(),
});
export type LeadNotesInput = z.infer<typeof leadNotesSchema>;

/**
 * Turning a lead into a customer — ROADMAP.md §9.9.
 *
 * **The name is split by a human**, exactly as it is when hiring an applicant:
 * `client` has a first and a last name and the quote form has one Name field.
 * `suggestNameSplit` in `./applicant.ts` prefills it; a person confirms it.
 *
 * The first property is optional and is prefilled from the quote when there is
 * one. A quote request carries bedrooms, bathrooms and a square-footage band but
 * **no street address** — the form never asks for one — so the address is
 * something the office types, and the property block is what tells them what
 * they are typing it for.
 */
export const convertLeadSchema = z.object({
  firstName: z.string().trim().min(1, 'Give a first name').max(128),
  lastName: z.string().trim().min(1, 'Give a last name').max(128),
  businessName: z
    .string()
    .trim()
    .max(255)
    .transform((value) => (value.length === 0 ? null : value))
    .nullable()
    .optional(),

  property: z
    .object({
      address: z.string().trim().min(1, 'Give a street address').max(255),
      city: z.string().trim().min(1, 'Which city?').max(128),
      state: z.string().trim().min(1, 'Which state?').max(64),
      zip: z.string().trim().min(3, 'Which zip code?').max(16),
      nickname: z
        .string()
        .trim()
        .max(128)
        .transform((value) => (value.length === 0 ? null : value))
        .nullable()
        .optional(),
    })
    .optional(),
});
export type ConvertLeadInput = z.infer<typeof convertLeadSchema>;

/**
 * What converting returns.
 *
 * `created` is false on a second call — conversion is idempotent and returns the
 * client the first call made rather than creating a second customer record for
 * one person.
 */
export interface ConvertLeadResult {
  leadId: string;
  clientId: string;
  clientName: string;
  created: boolean;
  /**
   * Whether the welcome email left the building.
   *
   * **`false` is not a failure of the conversion**, and the field exists so the
   * screen can say which happened instead of implying the customer has been
   * written to. `sendEmail` never throws: a send that fails leaves a `failed`
   * row in `email_log` with the sending server's own words on it, and the
   * client record stands either way.
   *
   * *Until 2026-08-05 `false` was the expected answer, because the server could
   * not send at all. It can now — see PROGRESS.md §Server.*
   */
  welcomeEmailSent: boolean;
}

/** Who a lead can be assigned to — active admins. */
export interface LeadAssignee {
  id: string;
  name: string;
}
